import React, { useEffect, useRef, useState } from "react";

// Lightweight CSS-3D molecule viewer — atoms as spheres, bonds as cylinders
// rotated with CSS transforms. No external 3D library dependency.

function Atom({ atom, colors }) {
  const c = colors[atom.el] || "#9ca3af";
  const size = (atom.r || 16) * 2;
  return (
    <div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        transform: `translate3d(${atom.x - size / 2}px, ${atom.y - size / 2}px, ${atom.z}px)`,
        background: `radial-gradient(circle at 30% 30%, #ffffff88, ${c} 45%, ${c}cc 100%)`,
        boxShadow: `0 0 16px ${c}80, inset -4px -4px 10px rgba(0,0,0,0.5)`,
      }}
      title={atom.el}
    />
  );
}

function Bond({ a, b, order }) {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const angleZ = (Math.atan2(dy, dx) * 180) / Math.PI;
  const angleY = -(Math.atan2(dz, Math.sqrt(dx * dx + dy * dy)) * 180) / Math.PI;
  const bonds = [];
  const offsets = order === 2 ? [-4, 4] : order === 3 ? [-6, 0, 6] : [0];
  for (let i = 0; i < offsets.length; i++) {
    bonds.push(
      <div
        key={i}
        className="absolute rounded-full"
        style={{
          width: len,
          height: 4,
          top: -2,
          left: 0,
          transform: `translate3d(${a.x}px, ${a.y + offsets[i]}px, ${a.z}px) rotateZ(${angleZ}deg) rotateY(${angleY}deg)`,
          transformOrigin: "0 50%",
          background: "linear-gradient(90deg, #ffffff33, #ffffffaa, #ffffff33)",
          boxShadow: "0 0 6px #ffffff55",
        }}
      />
    );
  }
  return <>{bonds}</>;
}

export default function MoleculeViewer({ molecule }) {
  const [rot, setRot] = useState({ x: -20, y: 30 });
  const [dragging, setDragging] = useState(false);
  const last = useRef(null);
  const autoRef = useRef(0);

  useEffect(() => {
    let raf;
    const tick = () => {
      if (!dragging) {
        autoRef.current += 0.35;
        setRot((r) => ({ x: r.x, y: r.y + 0.35 }));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dragging]);

  const onDown = (e) => { setDragging(true); last.current = { x: e.clientX, y: e.clientY }; };
  const onMove = (e) => {
    if (!dragging || !last.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setRot((r) => ({ x: Math.max(-80, Math.min(80, r.x - dy * 0.5)), y: r.y + dx * 0.5 }));
  };
  const onUp = () => { setDragging(false); last.current = null; };

  if (!molecule) return null;
  const colors = molecule.atom_color || {};

  return (
    <div
      className="relative h-72 select-none overflow-hidden rounded-md border border-white/10 bg-black/60"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      style={{ perspective: 900 }}
      data-testid="molecule-viewer"
    >
      <div className="absolute left-4 top-3 z-10">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">3D · drag to rotate</div>
        <div className="font-display text-lg font-semibold text-white">{molecule.name} · <span className="font-mono-num text-cyan-300">{molecule.formula}</span></div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          style={{
            transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
            transformStyle: "preserve-3d",
            width: 0,
            height: 0,
          }}
        >
          {molecule.bonds.map(([i, j, order], k) => (
            <Bond key={`b${k}`} a={molecule.atoms[i]} b={molecule.atoms[j]} order={order} />
          ))}
          {molecule.atoms.map((a, k) => (
            <Atom key={`a${k}`} atom={a} colors={colors} />
          ))}
        </div>
      </div>
    </div>
  );
}
